import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Briefcase, Plus, Edit2, Trash2, Copy, MapPin, Building, Eye, EyeOff, ExternalLink,
  Zap, Star, Calendar, Search, Users, TrendingUp, ArrowUpDown, CheckSquare, Square,
  Filter, X, Sparkles, Award, ClipboardCheck
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { JewelButton } from '../../components/ui/jewel-modal';
import { VacancyComposer } from '../../components/recruitment/VacancyComposer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SORT_OPTIONS = [
  { id: 'newest',    label: 'Newest', fn: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0) },
  { id: 'oldest',    label: 'Oldest', fn: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0) },
  { id: 'most_apps', label: 'Most applied', fn: (a, b) => (b.applications_count || 0) - (a.applications_count || 0) },
  { id: 'title',     label: 'A → Z',  fn: (a, b) => (a.title || '').localeCompare(b.title || '') },
];

const VacanciesPage = ({ getAuthHeaders, currentUser }) => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [prefillForNew, setPrefillForNew] = useState(null); // For duplicate action
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | draft | urgent
  const [deptFilter, setDeptFilter] = useState('');
  const [sortId, setSortId] = useState('newest');
  const [selected, setSelected] = useState(new Set());

  const canManage = ['executive', 'manager', 'superadmin', 'admin'].includes(currentUser?.role);

  const fetchVacancies = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/vacancies`, { headers });
      setVacancies(res.data);
    } catch { toast.error('Failed to load vacancies'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchVacancies(); }, []);

  if (!canManage) {
    return <div className="flex items-center justify-center h-96"><p className="text-white/40">Access denied. Management only.</p></div>;
  }

  const openNew = () => { setEditing(null); setPrefillForNew(null); setComposerOpen(true); };
  const openEdit = (v) => { setEditing(v); setPrefillForNew(null); setComposerOpen(true); };
  const duplicate = async (v) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/vacancies/${v.id}/duplicate`, {}, { headers });
      toast.success('Duplicated as draft');
      fetchVacancies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to duplicate'); }
  };
  const remove = async (id) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/admin/vacancies/${id}`, { headers });
      toast.success('Deleted');
      setDeleteConfirm(null);
      fetchVacancies();
    } catch { toast.error('Failed to delete'); }
  };
  const toggleActive = async (v) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/admin/vacancies/${v.id}`, { ...v, is_active: !v.is_active }, { headers });
      toast.success(v.is_active ? 'Deactivated' : 'Activated');
      fetchVacancies();
    } catch { toast.error('Failed to update'); }
  };

  const bulk = async (action) => {
    if (selected.size === 0) return;
    if (action === 'delete' && !window.confirm(`Delete ${selected.size} vacancies? This cannot be undone.`)) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/vacancies/bulk-action`,
        { vacancy_ids: Array.from(selected), action }, { headers });
      toast.success(`${res.data.affected} vacancies ${action.replace('_', ' ')}d`);
      setSelected(new Set());
      fetchVacancies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const departments = useMemo(() => {
    const set = new Set(vacancies.map(v => v.department).filter(Boolean));
    return Array.from(set).sort();
  }, [vacancies]);

  const filtered = useMemo(() => {
    let out = vacancies;
    if (filter === 'active') out = out.filter(v => v.is_active !== false);
    else if (filter === 'draft') out = out.filter(v => v.is_active === false);
    else if (filter === 'urgent') out = out.filter(v => v.is_urgent);
    if (deptFilter) out = out.filter(v => v.department === deptFilter);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(v => (v.title || '').toLowerCase().includes(q)
        || (v.department || '').toLowerCase().includes(q)
        || (v.location || '').toLowerCase().includes(q));
    }
    const sort = SORT_OPTIONS.find(s => s.id === sortId) || SORT_OPTIONS[0];
    return [...out].sort(sort.fn);
  }, [vacancies, filter, deptFilter, search, sortId]);

  const stats = {
    total: vacancies.length,
    active: vacancies.filter(v => v.is_active !== false).length,
    drafts: vacancies.filter(v => v.is_active === false).length,
    urgent: vacancies.filter(v => v.is_urgent).length,
    apps: vacancies.reduce((sum, v) => sum + (v.applications_count || 0), 0),
  };

  return (
    <div className="space-y-6 relative" data-testid="vacancies-page">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute -top-32 left-1/4 w-[500px] h-[300px] bg-cyan-500/[0.08] rounded-full blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[280px] bg-violet-500/[0.06] rounded-full blur-[120px]" />
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-6 md:p-7">
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-cyan-500/[0.10] rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-violet-500/[0.08] rounded-full blur-[100px]" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="relative w-14 h-14 flex-shrink-0">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
              <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
                <Briefcase className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">Recruitment · Vacancies</p>
              <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">
                Vacancy <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Studio</span>
              </h1>
              <p className="text-white/50 text-sm mt-1">Publish roles fast · AI-assisted descriptions · bulk actions · live preview</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openNew} data-testid="add-vacancy-btn"
              className="group relative inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white text-xs font-semibold shadow-[0_10px_30px_-10px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all overflow-hidden">
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Plus className="w-3.5 h-3.5" /> New vacancy
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total',       value: stats.total,   hex: '#00E5FF', Icon: Briefcase },
          { label: 'Active',      value: stats.active,  hex: '#10B981', Icon: Eye },
          { label: 'Drafts',      value: stats.drafts,  hex: '#94A3B8', Icon: EyeOff },
          { label: 'Urgent',      value: stats.urgent,  hex: '#F43F5E', Icon: Zap },
          { label: 'Applications',value: stats.apps,    hex: '#F59E0B', Icon: Users },
        ].map(s => (
          <div key={s.label} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 group hover:border-white/[0.15] transition-all">
            <div className="pointer-events-none absolute -top-8 -right-8 w-20 h-20 rounded-full blur-3xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ background: s.hex }} />
            <div className="relative flex items-center justify-between">
              <div>
                <s.Icon className="w-4 h-4 mb-2" style={{ color: s.hex }} />
                <p className="text-2xl font-light text-white tabular-nums leading-none">{s.value}</p>
                <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 mt-1.5">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar: filter chips + search + sort */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
            {[
              { id: 'all',    label: 'All',     count: stats.total,  hex: '#00E5FF' },
              { id: 'active', label: 'Active',  count: stats.active, hex: '#10B981' },
              { id: 'draft',  label: 'Drafts',  count: stats.drafts, hex: '#94A3B8' },
              { id: 'urgent', label: 'Urgent',  count: stats.urgent, hex: '#F43F5E' },
            ].map(t => {
              const a = filter === t.id;
              return (
                <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`vacancy-filter-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${
                    a ? 'text-white border border-white/20' : 'text-white/50 hover:text-white border border-transparent'
                  }`}
                  style={a ? { background: `linear-gradient(135deg, ${t.hex}22, ${t.hex}0a)`, boxShadow: `0 0 14px -4px ${t.hex}` } : {}}>
                  <span>{t.label}</span>
                  <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold ${a ? 'bg-white/20 text-white' : 'bg-white/[0.04] text-white/50'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {departments.length > 0 && (
            <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
              data-testid="vacancy-dept-filter"
              className="px-3 py-1.5 rounded-full bg-white/[0.02] border border-white/[0.06] text-white text-[11px] focus:outline-none focus:border-cyan-400/40">
              <option value="">All departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}

          <div className="flex items-center gap-1 ml-auto">
            <ArrowUpDown className="w-3 h-3 text-white/40" />
            <select value={sortId} onChange={(e) => setSortId(e.target.value)}
              data-testid="vacancy-sort"
              className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.06] text-white text-[11px] focus:outline-none focus:border-cyan-400/40">
              {SORT_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, department, location…"
            data-testid="vacancy-search"
            className="w-full pl-11 pr-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-full text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400/40" />
        </div>
      </div>

      {/* Bulk action bar (visible when selection > 0) */}
      {selected.size > 0 && (
        <div className="sticky top-4 z-20 rounded-2xl border border-cyan-400/40 bg-gradient-to-r from-cyan-500/[0.10] to-violet-500/[0.10] backdrop-blur-md p-3 flex items-center gap-2 flex-wrap animate-in fade-in" data-testid="bulk-action-bar">
          <ClipboardCheck className="w-4 h-4 text-cyan-300" />
          <p className="text-white/90 text-sm font-medium">{selected.size} selected</p>
          <div className="flex items-center gap-1.5 ml-auto flex-wrap">
            <BulkBtn onClick={() => bulk('activate')} testid="bulk-activate" hex="#10B981" icon={Eye} label="Activate" />
            <BulkBtn onClick={() => bulk('deactivate')} testid="bulk-deactivate" hex="#94A3B8" icon={EyeOff} label="Deactivate" />
            <BulkBtn onClick={() => bulk('mark_urgent')} testid="bulk-urgent" hex="#F43F5E" icon={Zap} label="Mark urgent" />
            <BulkBtn onClick={() => bulk('delete')} testid="bulk-delete" hex="#EF4444" icon={Trash2} label="Delete" />
            <button onClick={() => setSelected(new Set())} data-testid="bulk-clear"
              className="px-3 py-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/[0.06] text-[11px] font-medium transition-all">Clear</button>
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="text-center py-16 text-white/40 text-sm">Loading vacancies…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/[0.08] border border-cyan-500/20 mb-4">
            <Briefcase className="w-7 h-7 text-cyan-300" strokeWidth={1.5} />
          </div>
          <p className="text-white/70 tracking-wide">{search || filter !== 'all' ? 'No matches' : 'No vacancies yet'}</p>
          <p className="text-white/35 text-xs mt-1">
            {search || filter !== 'all' ? 'Adjust your filters or search.' : 'Tap "New vacancy" to publish your first role.'}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(v => (
            <VacancyCard key={v.id} v={v}
              isSelected={selected.has(v.id)}
              onToggleSelect={() => toggleSelect(v.id)}
              onEdit={() => openEdit(v)}
              onDelete={() => setDeleteConfirm(v)}
              onDuplicate={() => duplicate(v)}
              onToggleActive={() => toggleActive(v)}
            />
          ))}
        </div>
      )}

      {/* Composer modal */}
      <VacancyComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        editing={editing}
        prefill={prefillForNew}
        getAuthHeaders={getAuthHeaders}
        onSaved={() => { setComposerOpen(false); setEditing(null); fetchVacancies(); }}
      />

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="max-w-sm w-full rounded-2xl border border-rose-500/30 bg-[#0a0a14] p-6" data-testid="delete-vacancy-confirm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-rose-300" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Delete vacancy?</h3>
                <p className="text-white/50 text-xs">"{deleteConfirm.title}"</p>
              </div>
            </div>
            <p className="text-white/60 text-sm mb-4">This action cannot be undone. Existing applications will remain but be orphaned.</p>
            <div className="flex justify-end gap-2">
              <JewelButton variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</JewelButton>
              <JewelButton accent="rose" onClick={() => remove(deleteConfirm.id)} icon={Trash2} data-testid="delete-vacancy-confirm-btn">Delete</JewelButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BulkBtn = ({ onClick, testid, hex, icon: Icon, label }) => (
  <button onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-all"
    style={{ background: `${hex}15`, borderColor: `${hex}40`, color: hex }}>
    <Icon className="w-3 h-3" /> {label}
  </button>
);

const VacancyCard = ({ v, isSelected, onToggleSelect, onEdit, onDelete, onDuplicate, onToggleActive }) => {
  const isActive = v.is_active !== false;
  return (
    <div data-testid={`vacancy-card-${v.id}`}
      className={`group relative overflow-hidden rounded-2xl border transition-all p-5 ${
        isSelected
          ? 'border-cyan-400/60 bg-gradient-to-br from-cyan-500/[0.10] via-white/[0.02] to-violet-500/[0.06] shadow-[0_0_25px_-8px_rgba(0,229,255,0.4)]'
          : isActive
            ? 'border-white/[0.08] bg-white/[0.02] hover:border-cyan-400/30 hover:shadow-[0_0_20px_-10px_rgba(0,229,255,0.35)]'
            : 'border-white/[0.05] bg-white/[0.01] opacity-70 hover:opacity-100'
      }`}
    >
      {/* Selection checkbox */}
      <button onClick={onToggleSelect} data-testid={`select-${v.id}`}
        className="absolute top-3 left-3 z-10 w-6 h-6 rounded-md border flex items-center justify-center transition-all"
        style={{
          background: isSelected ? '#00E5FF22' : 'rgba(255,255,255,0.02)',
          borderColor: isSelected ? '#00E5FF80' : 'rgba(255,255,255,0.15)',
        }}>
        {isSelected ? <CheckSquare className="w-3.5 h-3.5 text-cyan-300" /> : <Square className="w-3.5 h-3.5 text-white/30" />}
      </button>

      <div className="flex items-start justify-between mb-3 pl-8">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            {v.is_urgent && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-[0.14em] uppercase bg-rose-500/20 border border-rose-400/40 text-rose-200 animate-pulse">
                <Zap className="w-2.5 h-2.5" /> Urgent
              </span>
            )}
            {!isActive && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-[0.14em] uppercase bg-white/[0.06] border border-white/[0.10] text-white/60">
                Draft
              </span>
            )}
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold tracking-[0.1em] uppercase bg-cyan-500/12 border border-cyan-400/30 text-cyan-200">{v.type}</span>
            {v.experience_level && <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold tracking-[0.1em] uppercase bg-violet-500/12 border border-violet-400/30 text-violet-200">{v.experience_level}</span>}
          </div>
          <h3 className="text-white font-semibold text-base leading-tight truncate">{v.title}</h3>
        </div>
      </div>

      <div className="pl-8 space-y-1 text-sm mb-3">
        <p className="text-white/60 flex items-center gap-2 text-[12.5px]"><Building className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />{v.department}</p>
        {v.location && <p className="text-white/60 flex items-center gap-2 text-[12.5px]"><MapPin className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />{v.location}</p>}
        {v.salary_range && <p className="text-emerald-300 flex items-center gap-2 text-[12.5px]"><span className="w-3.5 text-center font-bold text-emerald-400">£</span>{v.salary_range}</p>}
      </div>

      {v.description && <p className="text-white/45 text-[12px] line-clamp-2 mb-3 pl-8">{v.description}</p>}

      {/* Footer meta */}
      <div className="pt-3 border-t border-white/[0.05] pl-8 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-[10px] text-white/40">
          <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{v.created_at ? new Date(v.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Recent'}</span>
          {v.applications_count > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 font-bold" data-testid={`apps-count-${v.id}`}>
              <Users className="w-2.5 h-2.5" /> {v.applications_count} apps
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconBtn onClick={onToggleActive} testid={`toggle-active-${v.id}`} title={isActive ? 'Deactivate' : 'Activate'} hex={isActive ? '#10B981' : '#94A3B8'}>
            {isActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </IconBtn>
          <IconBtn onClick={onDuplicate} testid={`duplicate-${v.id}`} title="Duplicate" hex="#00E5FF">
            <Copy className="w-3.5 h-3.5" />
          </IconBtn>
          <IconBtn onClick={onEdit} testid={`edit-${v.id}`} title="Edit" hex="#8A2BE2">
            <Edit2 className="w-3.5 h-3.5" />
          </IconBtn>
          <IconBtn onClick={onDelete} testid={`delete-${v.id}`} title="Delete" hex="#F43F5E">
            <Trash2 className="w-3.5 h-3.5" />
          </IconBtn>
        </div>
      </div>
    </div>
  );
};

const IconBtn = ({ onClick, testid, title, hex, children }) => (
  <button onClick={onClick} data-testid={testid} title={title}
    className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
    style={{ color: hex, background: `${hex}0F` }}
    onMouseEnter={(e) => e.currentTarget.style.background = `${hex}25`}
    onMouseLeave={(e) => e.currentTarget.style.background = `${hex}0F`}>
    {children}
  </button>
);

export default VacanciesPage;
